import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useClientHealthSeries } from "../hooks/useClientHealthSeries";
import {
  computeHealthAverages,
  defaultMetricForSeries,
  filterRowsForMetric,
  sparseCategoryTicks,
} from "../lib/healthSeriesUtils";
import { formatNumber } from "../../../lib/formatNumber";
import NewColors from "../../../shared/theme/NewColors";

function StatCard({ label, value, unit = "" }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: "background.paper",
        height: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} color="primary" sx={{ mt: 0.5 }}>
        {value}
        {unit ? (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            {unit}
          </Typography>
        ) : null}
      </Typography>
    </Paper>
  );
}

/**
 * Health & nutrition summary + trends — aligned with coach Reports nutrition card (rolling window).
 */
function HealthSummaryPanel({ clientId, organizationId, weightUnit = "lbs" }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { chartRows, loading, error } = useClientHealthSeries({
    clientId,
    organizationId,
    days: 90,
  });

  const averages = useMemo(() => computeHealthAverages(chartRows), [chartRows]);

  const [metric, setMetric] = useState("kCal");
  const pickedDefault = useRef(false);

  useEffect(() => {
    pickedDefault.current = false;
  }, [clientId]);

  useEffect(() => {
    if (!chartRows?.length || pickedDefault.current) return;
    pickedDefault.current = true;
    setMetric(defaultMetricForSeries(chartRows));
  }, [chartRows]);

  const metricOptions = useMemo(
    () => [
      { value: "kCal", label: "Calories (kcal)" },
      { value: "proteins", label: "Protein (g)" },
      { value: "carbs", label: "Carbs (g)" },
      { value: "fats", label: "Fats (g)" },
      { value: "water", label: "Water (ml)" },
      { value: "fiber", label: "Fiber (g)" },
      { value: "weight", label: `Weight (${weightUnit})` },
      { value: "steps", label: "Steps" },
      { value: "sleepHours", label: "Sleep (hrs)" },
      { value: "averageHeartRate", label: "Heart rate (bpm)" },
    ],
    [weightUnit]
  );

  const filteredData = useMemo(() => filterRowsForMetric(metric, chartRows), [chartRows, metric]);

  const lineData = useMemo(() => {
    return filteredData.map((d) => ({
      ...d,
      _seriesValue: d[metric] ?? 0,
    }));
  }, [filteredData, metric]);

  const xTicks = useMemo(() => sparseCategoryTicks(lineData), [lineData]);

  const recentTable = useMemo(() => {
    return [...chartRows].sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime()).slice(0, 14);
  }, [chartRows]);

  const chartTitle = metricOptions.find((m) => m.value === metric)?.label ?? metric;

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {error.message ?? "Could not load health data."}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 6 }}>
        <CircularProgress size={32} />
        <Typography color="text.secondary">Loading your Health & nutrition…</Typography>
      </Box>
    );
  }

  if (!chartRows.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: (t) => `1px dashed ${t.palette.divider}`,
          bgcolor: NewColors.sectionBackground,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          When you log Health data from the mobile app, your trends and averages will appear here — same
          markers as on the Activity calendar and the same data your coach sees in Reports.
        </Typography>
      </Paper>
    );
  }

  const { counts } = averages;

  return (
    <Stack spacing={3}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <FavoriteIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h6" fontWeight={700} color="primary">
            Health & nutrition
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last 90 days of Health data — averages and trends (coach Reports–style summary).
          </Typography>
        </Box>
      </Box>

      <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
        Period averages
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
          (days with data only)
        </Typography>
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {counts.calories > 0 && (
          <Grid item xs={6} sm={4} md={3}>
            <StatCard label="Avg calories" value={formatNumber(averages.calories)} unit="kcal" />
          </Grid>
        )}
        {counts.proteins > 0 && (
          <Grid item xs={6} sm={4} md={3}>
            <StatCard label="Avg protein" value={formatNumber(averages.proteins)} unit="g" />
          </Grid>
        )}
        {counts.carbs > 0 && (
          <Grid item xs={6} sm={4} md={3}>
            <StatCard label="Avg carbs" value={formatNumber(averages.carbs)} unit="g" />
          </Grid>
        )}
        {counts.fats > 0 && (
          <Grid item xs={6} sm={4} md={3}>
            <StatCard label="Avg fats" value={formatNumber(averages.fats)} unit="g" />
          </Grid>
        )}
        {counts.water > 0 && (
          <Grid item xs={6} sm={4} md={3}>
            <StatCard label="Avg water" value={formatNumber(averages.water)} unit="ml" />
          </Grid>
        )}
        {counts.fiber > 0 && (
          <Grid item xs={6} sm={4} md={3}>
            <StatCard label="Avg fiber" value={formatNumber(averages.fiber)} unit="g" />
          </Grid>
        )}
        {counts.sleepHours > 0 && (
          <Grid item xs={6} sm={4} md={3}>
            <StatCard label="Avg sleep" value={String(averages.sleepHours)} unit="hrs" />
          </Grid>
        )}
        {counts.steps > 0 && (
          <Grid item xs={6} sm={4} md={3}>
            <StatCard label="Avg steps" value={formatNumber(averages.steps)} />
          </Grid>
        )}
        {counts.weight > 0 && (
          <Grid item xs={6} sm={4} md={3}>
            <StatCard label="Avg weight" value={formatNumber(averages.weight)} unit={weightUnit} />
          </Grid>
        )}
      </Grid>

      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 2, bgcolor: NewColors.cardBackground }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            {chartTitle}
          </Typography>
          <FormControl fullWidth size="small" sx={{ maxWidth: 400, mb: 2 }}>
            <InputLabel id="health-metric-label">Metric</InputLabel>
            <Select
              labelId="health-metric-label"
              label="Metric"
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
            >
              {metricOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {lineData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
              No data points for this metric in the last 90 days.
            </Typography>
          ) : (
            <Box sx={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineData} margin={{ top: 8, right: 12, left: 4, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={`${primary}18`} />
                  <XAxis
                    dataKey="createdOnString"
                    type="category"
                    ticks={xTicks}
                    interval="preserveStartEnd"
                    minTickGap={24}
                    tick={{ fontSize: 10, fill: NewColors.text.secondary }}
                    angle={-40}
                    textAnchor="end"
                    height={48}
                  />
                  <YAxis domain={[0, "auto"]} width={48} tick={{ fontSize: 11, fill: NewColors.text.secondary }} />
                  <Tooltip
                    formatter={(v) => [formatNumber(v), chartTitle]}
                    labelFormatter={(l) => `Day ${l}`}
                    contentStyle={{ borderRadius: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="_seriesValue"
                    stroke={primary}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={chartTitle}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>
      </Card>

      {recentTable.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            Recent days
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Calories</TableCell>
                  <TableCell align="right">Protein</TableCell>
                  <TableCell align="right">Carbs</TableCell>
                  <TableCell align="right">Fats</TableCell>
                  <TableCell align="right">Water (ml)</TableCell>
                  <TableCell align="right">Steps</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentTable.map((row, i) => (
                  <TableRow key={`${row.createdOnString}-${i}`}>
                    <TableCell>{row.createdOnString}</TableCell>
                    <TableCell align="right">{row.kCal > 0 ? formatNumber(row.kCal) : "—"}</TableCell>
                    <TableCell align="right">{row.proteins > 0 ? formatNumber(row.proteins) : "—"}</TableCell>
                    <TableCell align="right">{row.carbs > 0 ? formatNumber(row.carbs) : "—"}</TableCell>
                    <TableCell align="right">{row.fats > 0 ? formatNumber(row.fats) : "—"}</TableCell>
                    <TableCell align="right">{row.water > 0 ? formatNumber(row.water) : "—"}</TableCell>
                    <TableCell align="right">{row.steps > 0 ? formatNumber(row.steps) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Stack>
  );
}

export default HealthSummaryPanel;
